import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { API } from "@/App";
import { Calendar, Video, Copy, Check, ExternalLink, Rss, Link2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/utils";
import { useSiteContentCache } from "@/contexts/SiteContentCache";
import { NextGameCard } from "@/components/NextGameCard";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_crm-news-hub/artifacts/srv2lps9_ChatGPT%20Image%20Apr%202%2C%202026%2C%2010_47_10%20AM.png";

const slugify = (text) => String(text || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

// Invites come from the admin (Reeplayer Invites). When an invite has no explicit
// team, fall back to matching its label against synced TeamSnap team names.
const LABEL_TOKENS = (label) =>
  String(label || "").toLowerCase().match(/\d+[a-z]?/g) || [];

const resolveInvites = (rawInvites, teams) => {
  const taken = new Set();
  return rawInvites.map((invite) => {
    let team = teams.find((t) => t.team === invite.team);
    if (!team && !invite.team) {
      const tokens = LABEL_TOKENS(invite.label);
      team = teams.find((t) => {
        if (taken.has(t.slug)) return false;
        const name = t.team.toLowerCase();
        return tokens.length > 0 && tokens.some((token) => name.includes(token));
      });
    }
    if (team) taken.add(team.slug);
    return {
      ...invite,
      teamName: team?.team || invite.team || "",
      slug: team?.slug || "",
    };
  });
};

const ParentsPage = () => {
  const [teams, setTeams] = useState([]);
  const [invitesRaw, setInvitesRaw] = useState([]);
  const [nextGames, setNextGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);
  const { data: cachedContent } = useSiteContentCache();

  const logoUrl = cachedContent?.logo_url || cachedContent?.site_logo_url || LOGO_URL;
  const reeplayerLinks = cachedContent?.reeplayer?.reeplayer_links || [];

  useEffect(() => {
    axios.get(`${API}/schedule/teams`)
      .then((res) => setTeams(res.data.teams || []))
      .catch(() => setTeams([]))
      .finally(() => setLoading(false));
    axios.get(`${API}/reeplayer/invites`)
      .then((res) => setInvitesRaw(res.data.invites || []))
      .catch(() => setInvitesRaw([]));
    axios.get(`${API}/schedule/next-games`)
      .then((res) => setNextGames(res.data.games || []))
      .catch(() => setNextGames([]));
  }, []);

  const invites = useMemo(() => resolveInvites(invitesRaw, teams), [invitesRaw, teams]);

  const streamByTeam = useMemo(() => {
    const map = {};
    invites.forEach((invite) => {
      if (invite.slug) map[invite.slug] = { label: `${invite.label || invite.teamName} — Reeplayer invite`, url: invite.url };
    });
    reeplayerLinks.forEach((link) => {
      const key = slugify(link.team || link.label);
      if (key) map[key] = link;
    });
    return map;
  }, [reeplayerLinks, invites]);

  const unmatchedStreams = useMemo(() => {
    const teamSlugs = new Set(teams.map((t) => t.slug));
    return reeplayerLinks.filter((link) => !teamSlugs.has(slugify(link.team || link.label)));
  }, [reeplayerLinks, teams]);

  const copy = async (text, key) => {
    if (await copyToClipboard(text)) {
      setCopied(key);
      toast.success("Link copied — paste it in a text or group chat");
      setTimeout(() => setCopied(null), 2500);
    } else {
      toast.error("Could not copy — select the link manually");
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F4F3]">
      <Helmet>
        <title>Parent Hub | Commonwealth FC</title>
        <meta name="description" content="Commonwealth FC parent hub — team schedules, calendar subscriptions, and Reeplayer live stream links." />
        <meta name="robots" content="noindex" />
      </Helmet>

      <nav className="bg-[#0C2E3C] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-2">
            <img src={logoUrl} alt="Commonwealth FC" className="h-9 w-auto" />
            <span className="text-sm font-bold uppercase tracking-wider text-[#F0F4F3] hidden sm:block">Commonwealth FC</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/team-schedules" className="text-[#F0F4F3]/70 hover:text-[#C8E64E] text-sm font-medium transition-colors">All Schedules</Link>
            <Link to="/" className="text-[#F0F4F3]/70 hover:text-[#C8E64E] text-sm font-medium transition-colors">Home</Link>
          </div>
        </div>
      </nav>

      <header className="bg-[#0C2E3C] pb-14 pt-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block text-[10px] font-bold uppercase tracking-[0.2em] text-[#C8E64E] mb-3">For Commonwealth FC Families</span>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#F0F4F3] mb-3" data-testid="parents-page-title">
            Parent Hub
          </h1>
          <p className="text-[#F0F4F3]/60 max-w-2xl leading-relaxed">
            Everything your family needs in one place — your team&apos;s schedule, a calendar feed that updates itself, and the Reeplayer channel for watching games live.
          </p>
          <button
            onClick={() => copy(`${window.location.origin}/parents`, "page")}
            className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F0F4F3]/10 hover:bg-[#F0F4F3]/15 transition-colors text-[#F0F4F3] text-xs font-bold uppercase tracking-wider"
            data-testid="copy-page-link-btn"
          >
            {copied === "page" ? <Check size={13} className="text-[#C8E64E]" /> : <Copy size={13} className="text-[#C8E64E]" />}
            Share this page
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {nextGames.length > 0 && (
          <div className="mb-12" data-testid="next-games-section">
            <h2 className="text-base md:text-lg font-heading font-bold text-[#0C2E3C] mb-1">Next Up</h2>
            <p className="text-sm text-[#0C2E3C]/50 mb-5">The next game for each team — tap Directions for turn-by-turn to the field.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {nextGames.map((game, i) => (
                <NextGameCard key={game.slug} game={game} featured={i === 0} />
              ))}
            </div>
          </div>
        )}

        <h2 className="text-base md:text-lg font-heading font-bold text-[#0C2E3C] mb-1 flex items-center gap-2">
          <Users size={16} /> Your Teams
        </h2>
        <p className="text-sm text-[#0C2E3C]/50 mb-6">Tap a team for its full schedule, or subscribe so games land straight in your calendar.</p>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0C2E3C]" />
          </div>
        ) : teams.length === 0 ? (
          <div className="bg-white border border-[#0C2E3C]/10 rounded-sm p-10 text-center" data-testid="parents-teams-empty">
            <Calendar size={32} className="mx-auto text-[#0C2E3C]/20 mb-2" />
            <p className="text-sm text-[#0C2E3C]/50">Team schedules haven&apos;t been published yet — check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="parents-team-grid">
            {teams.map((t) => {
              const shareUrl = `${window.location.origin}/t/${t.slug}`;
              const feedUrl = `${API}/schedule/feed.ics?types=games_practices&teams=${encodeURIComponent(t.slug)}`;
              const googleUrl = `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(feedUrl)}`;
              const stream = streamByTeam[t.slug];
              return (
                <div key={t.slug} className="bg-white border border-[#0C2E3C]/10 rounded-sm p-5 hover:border-[#0C2E3C]/25 transition-colors" data-testid={`parents-team-card-${t.slug}`}>
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="min-w-0">
                      <h3 className="font-heading font-bold text-[#0C2E3C] text-lg leading-tight truncate">{t.team}</h3>
                      <p className="text-[11px] text-[#0C2E3C]/45 mt-0.5">{t.games} games · {t.total_events} events on the calendar</p>
                    </div>
                    <button
                      onClick={() => copy(shareUrl, t.slug)}
                      title="Copy shareable team link"
                      className="p-2 rounded-sm text-[#0C2E3C]/40 hover:text-[#0C2E3C] hover:bg-[#F0F4F3] transition-colors flex-shrink-0"
                      data-testid={`copy-team-link-${t.slug}`}
                    >
                      {copied === t.slug ? <Check size={15} /> : <Link2 size={15} />}
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <Link to={`/t/${t.slug}`} data-testid={`view-schedule-${t.slug}`}>
                      <Button className="w-full bg-[#0C2E3C] text-white hover:bg-[#0C2E3C]/90 font-bold text-[11px] uppercase tracking-wider rounded-sm">
                        <Calendar size={13} className="mr-1.5" /> Schedule
                      </Button>
                    </Link>
                    <a href={googleUrl} target="_blank" rel="noopener noreferrer" data-testid={`subscribe-${t.slug}`}>
                      <Button variant="outline" className="w-full border-[#0C2E3C]/20 text-[#0C2E3C] hover:bg-[#C8E64E]/20 font-bold text-[11px] uppercase tracking-wider rounded-sm">
                        <Rss size={13} className="mr-1.5" /> Subscribe
                      </Button>
                    </a>
                  </div>

                  {stream ? (
                    <a
                      href={stream.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 flex items-center gap-2 px-3 py-2 rounded-sm bg-[#0E6072]/10 hover:bg-[#0E6072]/20 transition-colors"
                      data-testid={`stream-link-${t.slug}`}
                    >
                      <Video size={14} className="text-[#0E6072] flex-shrink-0" />
                      <span className="text-xs font-medium text-[#0C2E3C] truncate flex-1">{stream.label || "Watch live on Reeplayer"}</span>
                      <ExternalLink size={11} className="text-[#0C2E3C]/40" />
                    </a>
                  ) : (
                    <p className="mt-3 text-[11px] text-[#0C2E3C]/35 px-1">No Reeplayer channel linked for this team yet</p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Reeplayer team invites */}
        <div className="mt-10 bg-[#0E6072]/[0.06] border border-[#0E6072]/20 rounded-sm p-5" data-testid="reeplayer-invites">
          <h2 className="text-base md:text-lg font-heading font-bold text-[#0C2E3C] mb-1 flex items-center gap-2">
            <Video size={16} className="text-[#0E6072]" /> Reeplayer Team Invites
          </h2>
          <p className="text-xs text-[#0C2E3C]/50 mb-4">Join your team&apos;s Reeplayer group once to watch games live and get notified when we go on air.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {invites.map((invite) => (
              <div
                key={invite.url}
                className="flex items-center gap-2 bg-white border border-[#0C2E3C]/10 rounded-sm px-3 py-2"
                data-testid={`invite-${slugify(invite.label)}`}
              >
                <span className="text-xs font-bold text-[#0C2E3C] truncate flex-1" title={invite.teamName || invite.label}>
                  {invite.teamName || invite.label}
                </span>
                <button
                  onClick={() => copy(invite.url, `invite-${invite.url}`)}
                  title="Copy invite link"
                  className="p-1 text-[#0C2E3C]/35 hover:text-[#0C2E3C] transition-colors"
                  data-testid={`copy-invite-${slugify(invite.label)}`}
                >
                  {copied === `invite-${invite.url}` ? <Check size={13} /> : <Link2 size={13} />}
                </button>
                <a
                  href={invite.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] font-bold uppercase tracking-wider text-[#0E6072] hover:text-[#0C2E3C] flex items-center gap-1 flex-shrink-0"
                  data-testid={`open-invite-${slugify(invite.label)}`}
                >
                  Join <ExternalLink size={10} />
                </a>
              </div>
            ))}
          </div>
        </div>

        {unmatchedStreams.length > 0 && (
          <div className="mt-12">
            <h2 className="text-base md:text-lg font-heading font-bold text-[#0C2E3C] mb-1 flex items-center gap-2">
              <Video size={16} /> Other Live Stream Channels
            </h2>
            <p className="text-sm text-[#0C2E3C]/50 mb-6">Reeplayer channels not yet matched to a team schedule.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="other-streams-grid">
              {unmatchedStreams.map((link) => (
                <a
                  key={link.id || link.label}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white border border-[#0C2E3C]/10 rounded-sm p-4 flex items-center gap-3 hover:border-[#0E6072]/40 transition-colors"
                  data-testid={`other-stream-${slugify(link.label)}`}
                >
                  <Video size={16} className="text-[#0E6072] flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-[#0C2E3C] truncate">{link.label}</p>
                    {link.description && <p className="text-[11px] text-[#0C2E3C]/45 truncate">{link.description}</p>}
                  </div>
                  <ExternalLink size={11} className="text-[#0C2E3C]/30" />
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="mt-12 bg-white border border-[#0C2E3C]/10 rounded-sm p-6">
          <h2 className="text-base md:text-lg font-heading font-bold text-[#0C2E3C] mb-2">Following more than one team?</h2>
          <p className="text-sm text-[#0C2E3C]/55 mb-4 max-w-2xl">
            Build a combined calendar with any mix of teams, and choose games only or games and practices.
          </p>
          <Link to="/team-schedules" data-testid="all-schedules-link">
            <Button className="bg-[#C8E64E] text-[#0C2E3C] hover:bg-[#C8E64E]/85 font-bold text-xs uppercase tracking-wider rounded-sm">
              Build a Custom Calendar
            </Button>
          </Link>
        </div>
      </div>

      <footer className="bg-[#0C2E3C] mt-16 py-8 text-center">
        <p className="text-[#F0F4F3]/50 text-xs">Commonwealth FC &middot; Charlottesville, VA</p>
      </footer>
    </div>
  );
};

export default ParentsPage;
